<template>
  <div
    class="sidebar__wrapper"
    :class="{ ['sidebar__mobile']: this.mobileMenuOpened }"
    ref="sidebar"
  >
    <div
      class="sidebar__mobile-button"
      :class="{ active: mobileMenuOpened, scrolled: scrollPositionTop }"
    >
      <img :src="mainLogo" alt="logo" />
      <VButton
        @click="toggleMobileMenu"
        type="primary"
        size="fit"
        noBorder
        extraClass="mobileButton"
        :icon="mobileMenuOpened ? 'times' : 'bars'"
        inverted
      ></VButton>
    </div>
    <div class="sidebar__logo" @click="navigate('/dashboard')">
      <img :src="mainIcon" alt="logo" />
    </div>
    <ul class="sidebar__list top">
      <VTooltip
        v-for="link in mainLinks"
        :text="link.label"
        :key="link.key"
        :extraClass="`sidebar ${link.key}`"
      >
        <li
          :class="{
            [`${link.label.toLowerCase()}`]: true,
            active: link.active,
            hovered: hovered === link.key
          }"
          @click="onClickLink(link)"
          @mouseover="hovered = link.key"
          @mouseleave="hovered = null"
        >
          <div class="sidebar__item-logo">
            <div class="sidebar__item-logo--top">
              <img :src="link.icon" alt="icon" />
            </div>

            <div class="sidebar__item-logo--bottom">
              <img :src="link.activeIcon" alt="icon" />
            </div>
          </div>

          <span class="sidebar__item">
            {{ link.label }}
          </span>
        </li>
      </VTooltip>
    </ul>
    <div class="sidebar__settings-mobile">
      <div
        class="title"
        :class="{
          active: _isSettingsRouteOpened
        }"
        @mouseover="hovered = 'settings'"
        @mouseleave="hovered = null"
        @click="onMobileSettingsClick"
      >
        <img :src="SettingsIcon" id="settings-icon" />
        <img :src="SettingsIconActive" id="settings-icon-active" />Settings
        <i
          class="chevron"
          :class="[
            this.mobileSettingsActive
              ? 'fa fa-chevron-up'
              : 'fa fa-chevron-down'
          ]"
        />
      </div>
      <transition name="fade">
        <ul
          v-show="mobileSettingsActive"
          :class="{ active: mobileSettingsActive }"
        >
          <li
            v-for="link in settingsList"
            @click="onClickLink(link, link.externalUrl)"
            :class="{ active: link.active }"
            :key="link.label"
          >
            {{ link.label }}
          </li>
        </ul></transition
      >
    </div>
    <ul class="sidebar__list">
      <VTooltip
        v-for="link in serviceLinks"
        :text="link.label"
        :key="link.key"
        :extraClass="`sidebar ${link.key}`"
      >
        <li
          :class="{
            active: link.active,
            hovered: hovered === link.key
          }"
          class="icon-wrapper"
          @click="onClickLink(link, link.externalUrl)"
          @mouseover="hovered = link.key"
          @mouseleave="hovered = null"
        >
          <div class="sidebar__item-logo">
            <div class="sidebar__item-logo--top">
              <img :src="link.icon" alt="icon" />
            </div>

            <div class="sidebar__item-logo--bottom">
              <img :src="link.activeIcon" alt="icon" />
            </div>
          </div>

          <span class="sidebar__item">
            {{ link.label }}
          </span>
        </li>
      </VTooltip>
      <VTooltip text="Settings" extraClass="sidebar settings">
        <div
          class="sidebar__avatar-icon-wrapper"
          :class="{ ['active']: _isSettingsRouteOpened }"
          @mouseover="settingsHovered = true"
          @mouseleave="settingsHovered = false"
          @click="navigate(onAvatarClickPath)"
        >
          <VAvatar
            v-if="getUser"
            bgColor="#ffca58"
            width="34px"
            height="34px"
            fontSize="12px"
            :firstName="getUser.first_name"
            :lastName="getUser.last_name"
            :image="getUser.profile_image_url"
            :useInitials="!getUser.profile_image_url"
            :isActive="settingsHovered"
          ></VAvatar>
          <span id="logout">Logout</span>
        </div>
      </VTooltip>
    </ul>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import { mapGetters } from "vuex";

// mixins
import withNavigate from "@/mixins/withNavigate";
import withScroll from "@/mixins/withScroll";

//utils
import { isCurrentRoute } from "@/utils/util";

// components
import VAvatar from "@/components/VAvatar";
import VTooltip from "@/components/VTooltip";
import VVueSelect from "@/components/VVueSelect";

// assets
import SettingsIcon from "@/assets/images/menu-icons-new/settings.svg";
import SettingsIconActive from "@/assets/images/menu-icons-new/settings-active.svg";
import DefaultAppLogo from "@/assets/images/logo-w-container.svg";
import toggleIcon from "@/assets/images/toggle-icon.svg";
import alertSvg from "@/assets/images/alert.svg";

import { getHashedUrl } from "@/utils/util";

export default Vue.component("TheSidebar", {
  props: {
    mainLinks: VueTypes.arrayOf(
      VueTypes.shape({
        key: VueTypes.string.isRequired,
        url: VueTypes.string.isRequired,
        label: VueTypes.string.isRequired,
        active: VueTypes.bool.def(false),
        icon: VueTypes.any,
        activeIcon: VueTypes.any
      })
    ),
    serviceLinks: VueTypes.arrayOf(
      VueTypes.shape({
        key: VueTypes.string.isRequired,
        url: VueTypes.string.isRequired,
        externalUrl: VueTypes.any,
        label: VueTypes.string.isRequired,
        active: VueTypes.bool.def(false),
        icon: VueTypes.any,
        activeIcon: VueTypes.any
      })
    ),
    mainIcon: VueTypes.any.def(DefaultAppLogo),
    mainLogo: VueTypes.any.def(DefaultAppLogo),
    hasAlert: VueTypes.bool.def(false),
    fullPath: VueTypes.string
  },

  components: {
    VAvatar,
    VTooltip
  },

  mixins: [withNavigate, withScroll()],

  data() {
    return {
      SettingsIcon,
      SettingsIconActive,
      alertSvg,
      toggleIcon,
      mobileMenuOpened: false,
      hovered: null,
      settingsHovered: false,
      mobileSettingsActive: false,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight
    };
  },

  computed: {
    settingsList() {
      const urls = {
        general: "/account/organization/general",
        returnAddress: "/account/organization/address",
        billing: "/account/organization/billing",
        preference: "/account/preferences",
        apiIntegrations: "/account/integrations",
        notifications: "/account/notifications",
        addressValidation: "/account/address-validation"
      };
      const { fullPath } = this.$route;
      return [
        {
          label: "General",
          url: urls.general,
          active: urls.general === fullPath
        },
        {
          label: "Return Address",
          url: urls.returnAddress,
          active: urls.returnAddress === fullPath
        },
        {
          label: "Billing",
          url: urls.billing,
          active: urls.billing === fullPath
        },
        {
          label: "Preference",
          url: urls.preference,
          active: urls.preference === fullPath
        },
        {
          label: "Api/Integrations",
          url: urls.apiIntegrations,
          active: urls.apiIntegrations === fullPath
        },
        {
          label: "Notifications",
          url: urls.notifications,
          active: urls.notifications === fullPath
        },
        {
          label: "Address Validation",
          url: urls.addresValidation,
          active: urls.addressValisation === fullPath
        },
        {
          label: "Status",
          url: "https://status.inkit.com/",
          externalUrl: true
        }
      ];
    },

    onAvatarClickPath() {
      return this.windowWidth < 991 ? "/logout" : "/account/settings";
    },

    _isSettingsRouteOpened() {
      const re = new RegExp("account");
      return re.exec(this.fullPath);
    },

    ...mapGetters("account", ["getUser"]),
    ...mapGetters("user", ["gravatar"])
  },

  methods: {
    _getGravatar() {
      return this.gravatar;
    },

    getUrlForLink(link) {
      return getHashedUrl(link.url);
    },

    toggleMobileMenu() {
      this.mobileMenuOpened = !this.mobileMenuOpened;
    },

    onClickLink(link, externalUrl) {
      if (externalUrl) {
        this.externalBlank(link.url);
      } else {
        this.navigate(link.url);
        this.mobileMenuOpened = false;
      }
    },

    getLinkIcon(link) {
      return link.active || this.hovered === link.key
        ? link.activeIcon
        : link.icon;
    },

    onMobileSettingsClick() {
      this.mobileSettingsActive = !this.mobileSettingsActive;
    },

    onWindowResize() {
      this.windowWidth = window.innerWidth;
      this.windowHeight = window.innerHeight;
    }
  },

  mounted() {
    if (this._isSettingsRouteOpened) {
      this.mobileSettingsActive = true;
    }

    window.addEventListener("resize", this.onWindowResize);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.onWindowResize);
  }
});
</script>

<style lang="scss" scoped>
$ns: "sidebar";

@mixin colorWithHover($color) {
  &,
  &:hover {
    color: $color;
  }
}

@mixin colorForActive($color) {
  .#{$ns}__item.is-active {
    @include colorWithHover($color);
  }
}

.#{$ns}__mobile-button {
  display: none;
}

.#{$ns}__logo {
  padding: 28px 0 0;
  text-align: center;
  cursor: pointer;

  img {
    width: 58px;
    height: 30px;
  }

}

.#{$ns}__wrapper {
  position: absolute;
  left: 0;
  flex: 0 0 $sidebar-width;
  background: transparent;
  -webkit-transition: left 0.3s ease-in-out;
  transition: left 0.3s ease-in-out;
  width: $sidebar-width;
  height: 100%;
  display: grid;
  grid-template-rows: min-content 1fr min-content;

  &.#{$ns}__mobile {
    left: 0;
  }

  &__mobile {
    display: none;
    position: absolute;
    top: 12px;
    right: 12px;
  }
}

.#{$ns}__list {
  list-style: none;
  padding: 0;
  display: flex;
  flex-direction: column;
  &.top {
    margin-top: 17px;
  }

  li {
    margin-top: 1px;
    border-radius: 0;
    border: none;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 17px 0;

    &:last-child {
      margin-bottom: 0;
    }

    .#{$ns}__item {
      font-size: 13px;
      text-align: center;
      transition: color 0.1s ease;
      color: #131e2c;
      text-decoration: none;
      display: none;

      &-logo {
        width: 26px;
        height: 26px;
        background-repeat: no-repeat;
        background-size: contain;
        transition: background-image 0.1s linear;
      }
    }

    &.active {
      background-color: #f5f7f9;
    }

    &.active,
    &.hovered {
      .#{$ns}__item {
        color: $primary-color;
      }
      .#{$ns}__item-logo {
        &--top {
          opacity: 0;
        }

        &--bottom {
          opacity: 1;
        }
      }
    }

    .#{$ns}__item-logo {
      position: relative;

      &--top,
      &--bottom {
        width: 100%;
        height: 100%;
        transition: opacity 0.1s linear;
      }

      img {
        position: absolute;
        left: 0;
        bottom: 0;
      }

      &--top {
        opacity: 1;
      }

      &--bottom {
        opacity: 0;
      }
    }

    &.dashboard {
    }
  }
}

.#{$ns}__settings-mobile {
  display: none;
}
.#{$ns}__avatar-icon-wrapper {
  padding: 18px 13px;
  position: relative;
  display: flex;
  align-items: center;

  &:hover {
    cursor: pointer;
    #logout {
      color: $primary-color;
    }
  }
}

#logout {
  display: none;
  &:hover {
    color: $primary-color;
  }
}

@media screen and (max-width: 991px), screen and (max-height: 600px) {
  .#{$ns}__wrapper {
    position: fixed;
    top: 0;
    left: -100vw;
    z-index: 9999;
    overflow: auto;
    /* @include customScrollbar(); */
    width: 100%;
    height: 100%;
    background: $main-body-color;
    grid-template-rows: repeat(3, min-content);

    @include hideScrollbar();
  }

  .#{$ns}__mobile-button {
    position: fixed;
    width: 100%;
    padding: 30px 20px 5px;
    background: white;
    right: 0;
    transition: all 0.4s ease-in-out;
    display: flex;
    justify-content: space-between;
    align-items: center;
    z-index: 2;
    &.scrolled {
      box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.15);
    }
    &.active {
      position: absolute;
      box-shadow: unset;
    }

  }

  .#{$ns}__list {
    font-size: 16px;
    &.top {
      margin-top: 98px;
    }

    li {
      display: flex;
      flex-direction: row;
      justify-content: left;
      padding-left: 20px;

      &.active {
        background-color: white;
      }

      .#{$ns}__item {
        display: block;
        font-size: 16px;
      }

      &:last-child {
        margin-right: 0;
      }
    }

    .#{$ns}__item-logo {
      margin-right: 24px;
    }
  }

  .#{$ns}__logo {
    display: none;
  }

  /* settings on mobile screen */
  .#{$ns}__settings-mobile {
    display: block;
    .title {
      display: grid;
      grid-auto-flow: column;
      align-items: center;
      grid-template-columns: min-content 1fr min-content;
      padding: 17px 20px;

      img {
        margin-right: 24px;
      }

      #settings-icon-active {
        display: none;
      }

      &:hover {
        cursor: pointer;
      }

      &:hover,
      &.active {
        padding: 17px 20px;
        color: $primary-color;
        #settings-icon {
          display: none;
        }
        #settings-icon-active {
          display: block;
        }
      }
    }

    .fade-enter-active,
    .fade-leave-active {
      transition: opacity 0.3s;
    }
    .fade-enter, .fade-leave-to /* .fade-leave-active до версии 2.1.8 */ {
      opacity: 0;
    }

    ul {
      display: grid;
      grid-gap: 35px;
      background: $soft-grey;
      box-shadow: inset 2px 4px 25px rgba(0, 0, 0, 0.1);
      padding: 35px 20px;
      transition: max-height 1s;
    }

    li {
      &.active {
        color: $primary-color;
      }
      transition: color 0.1s ease;
      &:hover {
        cursor: pointer;
        color: $primary-color;
      }
    }
  }

  .#{$ns}__avatar-icon-wrapper {
    padding: 17px 20px;
  }

  #logout {
    display: block;
    margin-left: 16px;
  }
}
</style>

<style lang="scss">
.sidebar__mobile-button button.mobileButton {
  i {
    font-size: 16px;
  }
}

.v-tooltip__wrapper.sidebar {
  [tooltip][flow^="right"] {
    &::before {
      left: 54px;
      transform: none;
      right: auto;
    }

    &::after {
      display: flex;
      padding: 0;
      max-width: 99px;
      font-size: 14px;
      left: 59px;
      font-family: Lato-Regular, sans-serif;
    }
  }

  &.settings {
    span {
      text-align: center;
    }

    [tooltip][flow^="right"] {
      &::before {
        left: 51px;
      }

      &::after {
        left: 56px;
      }
    }
  }
}

.v-avatar__wrapper {
  width: 70px;
  height: 70px;
  max-width: 70px;
  background-color: rgb(255, 202, 88);
  font-size: 24px;
}

@media screen and (max-width: 991px) {
  .v-tooltip__wrapper.sidebar {
    min-width: 100%;
    [tooltip][flow^="right"] {
      &::before,
      &::after {
        display: none;
      }
    }
  }
}
</style>
